/**
 * QuickBooks Connection Card
 * Displays on client detail page for managing QBO integration
 */

import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { Cloud, RefreshCw, Unplug, AlertCircle, CheckCircle, Clock } from 'lucide-react'
import { formatDate } from '@/lib/format'
import {
  useQBOConnection,
  useQBOConnect,
  useQBOSync,
  useQBODisconnect,
} from '../hooks/useQuickBooks'

interface QuickBooksCardProps {
  clientId: string
}

export function QuickBooksCard({ clientId }: QuickBooksCardProps) {
  const { data: connection, isLoading } = useQBOConnection(clientId)
  const connectMutation = useQBOConnect()
  const syncMutation = useQBOSync()
  const disconnectMutation = useQBODisconnect()
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false)

  const handleConnect = async () => {
    try {
      const result = await connectMutation.mutateAsync(clientId)
      // Redirect to QuickBooks authorization page
      window.location.href = result.auth_url
    } catch (error) {
      console.error('Failed to connect QuickBooks:', error)
      alert('Failed to initiate QuickBooks connection. Please try again.')
    }
  }

  const handleSync = async () => {
    try {
      const result = await syncMutation.mutateAsync(clientId)
      alert(
        `Sync completed!\n\nCustomers: ${result.customersUpserted}\nInvoices: ${result.invoicesUpserted}\nPayments Detected: ${result.paymentsDetected}`
      )
    } catch (error) {
      console.error('Sync failed:', error)
      alert('Sync failed. Please try again.')
    }
  }

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync(clientId)
      setShowDisconnectConfirm(false)
      alert('QuickBooks disconnected successfully')
    } catch (error) {
      console.error('Failed to disconnect:', error)
      alert('Failed to disconnect QuickBooks. Please try again.')
    }
  }

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Cloud className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">QuickBooks Online</h3>
              <p className="text-sm text-gray-500">Loading...</p>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  // Not connected
  if (!connection) {
    return (
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Cloud className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">QuickBooks Online</h3>
              <p className="text-sm text-gray-500">Not connected</p>
            </div>
          </div>
          <Button
            onClick={handleConnect}
            loading={connectMutation.isPending}
            className="flex items-center space-x-2"
          >
            <Cloud className="h-4 w-4" />
            <span>Connect QuickBooks</span>
          </Button>
        </div>
      </Card>
    )
  }

  // Connected
  const isConnected = connection.status === 'connected'
  const isExpired = connection.status === 'expired'
  const hasError = connection.status === 'error'

  return (
    <>
      <Card>
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${
                isConnected ? 'bg-green-100' : hasError ? 'bg-red-100' : 'bg-yellow-100'
              }`}>
                <Cloud className={`h-5 w-5 ${
                  isConnected ? 'text-green-600' : hasError ? 'text-red-600' : 'text-yellow-600'
                }`} />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">QuickBooks Online</h3>
                <div className="flex items-center space-x-2 mt-1">
                  {isConnected && (
                    <Badge variant="success" className="flex items-center space-x-1">
                      <CheckCircle className="h-3 w-3" />
                      <span>Connected</span>
                    </Badge>
                  )}
                  {isExpired && (
                    <Badge variant="warning" className="flex items-center space-x-1">
                      <Clock className="h-3 w-3" />
                      <span>Expired</span>
                    </Badge>
                  )}
                  {hasError && (
                    <Badge variant="danger" className="flex items-center space-x-1">
                      <AlertCircle className="h-3 w-3" />
                      <span>Error</span>
                    </Badge>
                  )}
                  <Badge variant={connection.environment === 'production' ? 'default' : 'warning'}>
                    {connection.environment === 'production' ? 'Production' : 'Sandbox'}
                  </Badge>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {isConnected && (
                <Button
                  variant="secondary"
                  onClick={handleSync}
                  loading={syncMutation.isPending}
                  className="flex items-center space-x-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Sync Now</span>
                </Button>
              )}
              {isExpired && (
                <Button
                  onClick={handleConnect}
                  loading={connectMutation.isPending}
                  className="flex items-center space-x-2"
                >
                  <Cloud className="h-4 w-4" />
                  <span>Reconnect</span>
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => setShowDisconnectConfirm(true)}
                className="flex items-center space-x-2"
              >
                <Unplug className="h-4 w-4" />
                <span>Disconnect</span>
              </Button>
            </div>
          </div>

          {/* Status Info */}
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
            <div>
              <p className="text-sm text-gray-500">Last Synced</p>
              <p className="text-sm font-medium text-gray-900">
                {connection.last_sync_at
                  ? formatDate(connection.last_sync_at)
                  : 'Never'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Company ID</p>
              <p className="text-sm font-medium text-gray-900 font-mono">
                {connection.realm_id}
              </p>
            </div>
          </div>

          {/* Error Message */}
          {connection.last_error && (
            <div className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">Error</p>
                <p className="text-sm text-red-700 mt-1">{connection.last_error}</p>
              </div>
            </div>
          )}

          {/* Sync Success Toast */}
          {syncMutation.isSuccess && (
            <div className="flex items-start space-x-2 p-3 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">Sync Completed</p>
                <p className="text-sm text-green-700 mt-1">
                  {syncMutation.data.customersUpserted} customers, {syncMutation.data.invoicesUpserted} invoices
                  {syncMutation.data.paymentsDetected > 0 &&
                    `, ${syncMutation.data.paymentsDetected} payments detected`}
                </p>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Disconnect Confirmation Modal */}
      <Modal
        isOpen={showDisconnectConfirm}
        onClose={() => setShowDisconnectConfirm(false)}
        title="Disconnect QuickBooks?"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to disconnect QuickBooks? This will:
          </p>
          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
            <li>Revoke access to this client's QuickBooks data</li>
            <li>Stop automatic syncing</li>
            <li>Keep existing customers and invoices (they won't be deleted)</li>
          </ul>
          <p className="text-sm text-gray-600">
            You can reconnect at any time.
          </p>
          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => setShowDisconnectConfirm(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDisconnect}
              loading={disconnectMutation.isPending}
            >
              Disconnect
            </Button>
          </div>
        </div>
      </Modal>
    </>
  )
}
